import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LayoutTemplate, ArrowRight, Dices, Loader2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const SURPRISES = [
  { template: "kart3d", idea: "A speedy go-kart race around a candy-themed loop circuit", style: "kart racing" },
  { template: "fps3d", idea: "A first-person arena where a hero blasts waves of goofy slime invaders", style: "first-person shooter" },
  { template: "tower3d", idea: "Defend a crystal castle by placing towers along a winding path", style: "tower defense" },
  { template: "explorer3d", idea: "A brave fox explores a glowing 3D forest collecting magic acorns", style: "open-world adventure" },
  { template: "platformer3d", idea: "Hop across floating sky islands to reach the rainbow crystal", style: "3D platformer" },
  { template: "runner3d", idea: "Auto-run through a neon city dodging obstacles and grabbing coins", style: "endless runner" },
  { template: "shooter3d", idea: "Blast waves of wobbly UFOs in a starry space arena", style: "space shooter" },
  { template: "racing3d", idea: "Speed down a busy highway dodging traffic and grabbing coins", style: "arcade racing" },
  { template: "platformer", idea: "A classic side-scrolling jump adventure collecting shiny coins", style: "classic platformer" },
  { template: "maze3d", idea: "Find your way through a tricky 3D hedge maze collecting keys", style: "maze puzzle" },
];

export default function Templates() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState(null);
  const [surprising, setSurprising] = useState(false);
  const [q, setQ] = useState(searchParams.get("q") || "");
  useEffect(() => { api.get("/templates").then((r) => setTemplates(r.data)); }, []);

  const surprise = async () => {
    if (surprising) return;
    setSurprising(true);
    const pick = SURPRISES[Math.floor(Math.random() * SURPRISES.length)];
    try {
      const { data: planData } = await api.post("/ai/plan", { idea: pick.idea, options: { template: pick.template, style: pick.style } });
      const { data: game } = await api.post("/games", {
        title: planData.plan?.title || "Surprise Game", idea: pick.idea, plan: planData.plan, config: planData.config,
      });
      toast.success("Studio rolled you a surprise game! 🎲");
      navigate(`/app/build/${game.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't roll a surprise — try again!");
      setSurprising(false);
    }
  };

  if (!templates) return <Spinner />;
  const term = q.trim().toLowerCase();
  const shown = term ? templates.filter((t) => [t.name, t.genre, t.description].filter(Boolean).some((s) => String(s).toLowerCase().includes(term))) : templates;

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto">
      <PageHeader title="Game Templates" icon={LayoutTemplate} subtitle="Studio builds reliable, playable games on these proven templates." />

      <div className="relative mb-5 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates by name or genre…" className="pl-9 rounded-full" data-testid="templates-search" />
      </div>

      <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/12 to-transparent p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="surprise-me-card">
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><Dices className="w-5 h-5 text-[hsl(var(--primary))]" /> Feeling lucky?</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Let Studio roll the dice and instantly build a playable game from a random engine — racing, FPS, tower defense and more.</p>
        </div>
        <Button onClick={surprise} disabled={surprising} className="rounded-full gap-2 shrink-0" data-testid="surprise-me-btn">
          {surprising ? <><Loader2 className="w-4 h-4 animate-spin" /> Rolling the dice…</> : <><Dices className="w-4 h-4" /> Surprise me</>}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((t, i) => (
          <div key={t.id} className={`rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 card-hover animate-rise delay-${(i % 5) + 1}`}>
            <div className="w-14 h-14 rounded-2xl grid place-items-center text-3xl mb-4" style={{ background: `${t.color}22` }}>{t.icon}</div>
            <h3 className="font-display text-xl font-bold">{t.name}</h3>
            <div className="text-xs font-semibold mb-2" style={{ color: t.color }}>{t.genre}{t.dimension === "3d" && " · 3D"}</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{t.description}</p>
            <Button variant="outline" size="sm" className="rounded-full w-full" onClick={() => navigate("/app/create")} data-testid={`template-use-${t.id}`}>
              Build with this <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
